import React, { Dispatch, SetStateAction, useMemo } from 'react';
import {
  CareBenefitUsageToInvoiceLineItem,
  ClinicPet,
  EnrollmentStatus,
  SidePanelParentPetDataFragment,
  SpeciesType,
} from 'shared/types/graphql';
import { colorLightenDarken, getMonetaryDisplayValue } from 'shared/utils';
import styled from 'styled-components/macro';
import PetAvatar from 'shared/components/Avatars/PetAvatar';
import CareBadge from 'shared/components/Badges/CareBadge';
import { FilteredLineItemsType } from './Invoice';
import InvoiceLineItemTable from './InvoiceLineItemTable';
import { IAdjustment } from 'shared/components/SidePanel/components/SearchPanel/components/Benefits/hooks/useMarkPetBenefitUsage';
import uniqBy from 'lodash-es/uniqBy';
import {
  Box,
  HStack,
  Table,
  TableCaption,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Text,
} from '@televet/kibble-ui';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IInvoiceLineItemTableListProps {
  filteredLineItems: FilteredLineItemsType;
  isMobile: boolean;
  patientDiscounts?: Record<string, number>;
  setPatientDiscounts?: Dispatch<SetStateAction<Record<string, number>>>;
  adjustments: IAdjustment[];
  isPrintMode?: boolean;
  fromInvoicePreview?: boolean;
  isReceipt?: boolean;
}

interface IPetLineItems {
  pet: ClinicPet | SidePanelParentPetDataFragment;
  lineItems: FilteredLineItemsType;
  filteredByPetTotal: number;
  connectedCareBenefits: CareBenefitUsageToInvoiceLineItem[] | undefined;
}

const InvoiceLineItemTableList = ({
  filteredLineItems,
  isMobile,
  patientDiscounts,
  setPatientDiscounts,
  adjustments,
  isPrintMode,
  fromInvoicePreview,
  isReceipt,
}: IInvoiceLineItemTableListProps): JSX.Element => {
  const { isPatientIdSupported } = useIntegrationsProvider();
  const { trackEvent } = useTracking();

  const filteredLineItemsByPet = useMemo(() => {
    const lineItemsByPet: IPetLineItems[] = [];

    const lineItemPets = filteredLineItems.map((item) => item.clinicPets);

    if (lineItemPets.length === 0) return [];

    const flatUniquePets = uniqBy(lineItemPets.flat(), 'id');
    flatUniquePets.forEach((pet) => {
      const lineItems = filteredLineItems.filter((item) => item.clinicPets.includes(pet));

      lineItemsByPet.push({
        pet,
        lineItems,
        filteredByPetTotal: lineItems.reduce((acc, item) => acc + item.amount + item.fee - item.discount, 0),
        connectedCareBenefits: lineItems.flatMap((item) => item.connectedCareBenefits),
      });
    });

    return lineItemsByPet;
  }, [filteredLineItems]);

  //Additional line items not associated with any pet, just the relevant pet parent
  const petlessLineItems: FilteredLineItemsType = useMemo(() => {
    if (filteredLineItems.length === 0) return [];
    const lineItemPets = filteredLineItems.map((item) => item.clinicPets);
    if (lineItemPets.length === 0) return filteredLineItems;
    return filteredLineItems.filter((item) => item.clinicPets.length === 0);
  }, [filteredLineItems]);

  const onlyOnePet = useMemo(() => {
    return filteredLineItemsByPet.length === 1;
  }, [filteredLineItemsByPet]);

  const defaultIndices = new Array(filteredLineItemsByPet.length).fill(0).map((_, i) => i);

  return (
    <>
      {!!filteredLineItemsByPet.length &&
        filteredLineItemsByPet.map((petLineItem) => {
          const enrollment = petLineItem.pet.organizationPet?.carePlanEnrollments.find(
            (enrollment) => enrollment.status === EnrollmentStatus.Active,
          );
          return (
            <React.Fragment key={`${petLineItem.pet.id}-{index}`}>
              <Accordion
                allowMultiple
                textColor="text.default"
                defaultIndex={defaultIndices}
                index={isPrintMode ? defaultIndices : undefined}
                className="Invoice__InvoiceLineItemTableList"
              >
                <AccordionItem borderRadius="5" mt="3">
                  {({ isExpanded }: { isExpanded: boolean; isDisabled: boolean }): JSX.Element => (
                    <>
                      <StyledHeaderWrapper>
                        {/* AccordianButton must be wrapped in a heading element per chakra's docs https://chakra-ui.com/docs/components/disclosure/accordion */}
                        <AccordionButton
                          _hover={{ bg: colorLightenDarken('#EAEBF1' || '#000', -15) }}
                          bgColor="background.lessSubtle"
                          cursor="pointer"
                          onClick={(): void => trackEvent(ConversationEvents.InvoiceTogglePetDetails, { isExpanded })}
                        >
                          <Box flex="1" textAlign="left">
                            <HStack justifyContent="space-between">
                              <HStack w="100%" justifyContent="center">
                                <HStack spacing={1}>
                                  <PetAvatar
                                    speciesType={petLineItem.pet.speciesType}
                                    isDeceased={petLineItem.pet.computedIsDeceased}
                                    iconOnly
                                  />
                                  <Text fontWeight="bold">{petLineItem.pet.name}</Text>
                                  {!!enrollment && <CareBadge isCareMember={!!enrollment} />}
                                </HStack>

                                <HStack mr="auto !important">
                                  {isPatientIdSupported && (
                                    <Box display="flex" mx="12">
                                      <Text>{`${isMobile ? `ID: ` : `Patient ID: `}`}</Text>
                                      <Text fontWeight="bold" ml="1">
                                        {petLineItem.pet.pimsId}
                                      </Text>
                                    </Box>
                                  )}
                                  {!isMobile && (
                                    <HStack spacing="12">
                                      <HStack>
                                        <Box>Species:</Box>
                                        <Text fontWeight="bold">
                                          {!petLineItem.pet.species
                                            ? 'not specified'
                                            : petLineItem.pet.speciesType === SpeciesType.Other
                                            ? petLineItem.pet.species
                                            : petLineItem.pet.speciesType}
                                        </Text>
                                      </HStack>
                                      <HStack>
                                        <Box>Weight:</Box>
                                        <Text fontWeight="bold">
                                          {petLineItem.pet.weight
                                            ? `${petLineItem.pet.weight?.toFixed(2)} lbs`
                                            : 'not specified'}
                                        </Text>
                                      </HStack>
                                    </HStack>
                                  )}
                                </HStack>
                              </HStack>
                              {!onlyOnePet && (
                                <HStack>
                                  <Box
                                    fontWeight="bold"
                                    textDecoration={onlyOnePet ? 'none' : 'underline'}
                                    ml={isMobile ? 0 : 10}
                                  >
                                    Details
                                  </Box>
                                  <AccordionIcon />
                                </HStack>
                              )}
                            </HStack>
                            {!isExpanded && !isReceipt && (
                              <HStack pt="4" justifyContent="space-between">
                                <Box>
                                  PATIENT SUBTOTAL:{' '}
                                  {getMonetaryDisplayValue(
                                    fromInvoicePreview
                                      ? petLineItem.filteredByPetTotal -
                                          (petLineItem.connectedCareBenefits
                                            ? petLineItem.connectedCareBenefits.reduce(
                                                (acc, item) => acc + item.adjustedAmount,
                                                0,
                                              )
                                            : 0)
                                      : petLineItem.filteredByPetTotal -
                                          (patientDiscounts ? patientDiscounts[petLineItem.pet.id] : 0),
                                    true,
                                  )}
                                </Box>
                              </HStack>
                            )}
                          </Box>
                        </AccordionButton>
                      </StyledHeaderWrapper>
                      <AccordionPanel bgColor="background.subtle" p="0 !important" w="100%">
                        <StyledTable
                          variant="unstyled"
                          p="0 !important"
                          textColor="text.default"
                          borderBottom="0 !important"
                        >
                          <TableCaption
                            bgColor="background.subtle"
                            mt="0 !important"
                            pt="6"
                            textColor="text.default"
                            borderTop="1px"
                          >
                            {!isReceipt && (
                              <HStack justifyContent="space-between">
                                <Box>
                                  PATIENT SUBTOTAL:{' '}
                                  {getMonetaryDisplayValue(
                                    fromInvoicePreview
                                      ? petLineItem.filteredByPetTotal -
                                          (petLineItem.connectedCareBenefits
                                            ? petLineItem.connectedCareBenefits.reduce(
                                                (acc, item) => acc + item.adjustedAmount,
                                                0,
                                              )
                                            : 0)
                                      : petLineItem.filteredByPetTotal -
                                          (patientDiscounts ? patientDiscounts[petLineItem.pet.id] : 0),
                                    true,
                                  )}
                                </Box>
                              </HStack>
                            )}
                          </TableCaption>
                          <InvoiceLineItemTable
                            filteredLineItems={petLineItem.lineItems}
                            isMobile={isMobile}
                            clinicPetId={petLineItem.pet.id}
                            setPatientDiscounts={setPatientDiscounts}
                            adjustments={adjustments}
                            fromInvoicePreview={fromInvoicePreview}
                          />
                        </StyledTable>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              </Accordion>
            </React.Fragment>
          );
        })}
      {!!petlessLineItems.length && (
        <>
          <StyledTable variant="unstyled" bgColor="background.default" p="0 !important" textColor="text.default">
            <TableCaption bgColor="background.default" fontWeight="bold" placement="top">
              ADDITIONAL LINE ITEMS
            </TableCaption>
            <InvoiceLineItemTable
              clinicPetId=""
              filteredLineItems={petlessLineItems}
              adjustments={adjustments}
              isMobile={isMobile}
            />
          </StyledTable>
        </>
      )}
    </>
  );
};

export default InvoiceLineItemTableList;

const StyledHeaderWrapper = styled.h2`
  overflow: hidden;
  border-radius: 5px 5px 0 0;
`;

const StyledTable = styled(Table)`
  th,
  td {
    padding: 12px;
  }
`;
